"use client";

import { Fragment } from "react";
import { Dialog, DialogPanel, DialogTitle, DialogBackdrop, Transition } from "@headlessui/react-v2";
import { XMarkIcon } from "@heroicons/react/24/outline";
import YAButton from "../inputs/button";
import { translate } from "@/lib/translator";
import { classNames } from "@/lib/classnames";
import YAScrollContainer from "./scroll-container";

export default function YAModal({
  open,
  setOpen,
  onAfterLeave,
  onBeforeEnter,
  onFormSubmit,
  closeButtonText,
  submitButtonText = translate("General.Save"),
  title = "",
  panelClass = "max-w-lg",
  isFormValid,
  footer = true,
  loading,
  isLoading,
  unmount = true,
  children,
}) {

  return (
    <>
      <Transition
        appear
        unmount={unmount} 
        show={open}
        as={Fragment}
        afterLeave={() => {
          onAfterLeave && onAfterLeave();
        }}
        beforeEnter={() => {
          onBeforeEnter && onBeforeEnter();
        }}
      >
        <Dialog
          as="div"
          onClose={() => setOpen(false)} 
          className="relative z-[999]"
        >
          <DialogBackdrop transition className="fixed inset-0 bg-black/75 duration-300 ease-out data-[closed]:opacity-0" />

          <div className="fixed inset-0 flex items-start justify-center px-4 py-10 text-center">
              <DialogPanel
                transition
                className={classNames(
                  "w-screen h-full pointer-events-none duration-300 ease-out data-[closed]:opacity-0",
                  panelClass
                )}
              >
                <form
                  onSubmit={onFormSubmit}
                  className="pointer-events-auto relative flex max-h-full flex-col bg-white border-b-4 border-ymcagray-200 shadow-xl rounded-lg overflow-hidden"
                >
                  <div className="px-4 py-4 h-16 border-b border-b-ymcagray-100 flex items-center">
                    <div className="flex items-center justify-between flex-1">
                      <DialogTitle className="text-base font-semibold leading-6 text-ymcared-700">
                        {title}
                      </DialogTitle>
                      
                      <div className="ml-3 flex items-center">
                        <YAButton
                          icon={closeButtonText ? null : XMarkIcon}
                          value={closeButtonText}
                          variant="basic-dark"
                          onClick={() => setOpen(false)}
                        />
                      </div>
                    </div>
                  </div>

                  <YAScrollContainer
                    className="flex-1 overflow-auto flex flex-col persist-scrollbar"
                    scrollbarSpaceTop={6}
                    scrollbarSpaceBottom={6}
                    scrollbarSpaceRight={5}
                  >
                    <div className=" p-4 flex flex-1 flex-col justify-between">
                      {children}
                    </div>
                  </YAScrollContainer>

                  {/* {Phase this out, start using isLoading instead} */}
                  {loading && loading}

                  {isLoading && (
                    <div className="absolute inset-0 z-50 bg-white-50">
                      <div className="h-16"></div>
                      <div className="h-2 w-full bg-blue-400 overflow-hidden sticky top-16">
                        <div className="h-full custom-stripes"></div>
                      </div>
                    </div>
                  )}

                  {footer && (
                    <div className="px-4 py-4 relative">
                      <div className="grid grid-cols-2 gap-3">
                        <YAButton
                          value={submitButtonText}
                          variant="danger"
                          className="!rounded-md"
                          textClassName="text-center uppercase"
                          disabled={isFormValid === false ? true : false}
                          submit={true}
                        />

                        <YAButton
                          value={translate("General.Cancel")}
                          variant="gray-light"
                          className="!rounded-md"
                          textClassName="text-center uppercase"
                          onClick={() => setOpen(false)}
                        />
                      </div>
                    </div>
                  )}
                </form>
              </DialogPanel>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
